import * as THREE from 'three';
import { BoardView } from './boardView.ts';
import { PieceViewManager } from './pieceView.ts';
import { CastleEnvironment } from './environment.ts';
import { CameraController } from './cameraController.ts';
import { AnimationManager } from './animationManager.ts';
import { SoundManager } from '../audio/soundManager.ts';
import { Square } from '../core/types.ts';

export class SceneManager {
  private container: HTMLElement;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private clock: THREE.Clock;

  // Sub-systems
  private cameraController: CameraController;
  private boardView: BoardView;
  private pieceView: PieceViewManager;
  private environment: CastleEnvironment;
  private animationManager: AnimationManager;

  // Lighting
  private dirLight: THREE.DirectionalLight;
  private ambientLight: THREE.AmbientLight;

  // Interaction Raycaster
  private raycaster: THREE.Raycaster;
  private mouse: THREE.Vector2;

  // Loop control
  private isRunning = true;
  private animFrameId: number | null = null;

  constructor(container: HTMLElement, soundManager: SoundManager) {
    this.container = container;
    this.clock = new THREE.Clock();

    // 1. Scene with atmospheric fog
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0e1118, 0.016);

    // 2. Camera
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;
    this.camera = new THREE.PerspectiveCamera(45, width / height, 0.5, 120);

    // 3. WebGL Renderer
    this.renderer = new THREE.WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance'
    });
    this.renderer.setSize(width, height);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.05;
    this.container.appendChild(this.renderer.domElement);

    // 4. Lights
    this.ambientLight = new THREE.AmbientLight(0x2d3748, 1.2);
    this.scene.add(this.ambientLight);

    // Dramatic high moon skylight
    this.dirLight = new THREE.DirectionalLight(0xe2ecfb, 2.8);
    this.dirLight.position.set(12, 22, 14);
    this.dirLight.castShadow = true;
    this.dirLight.shadow.mapSize.width = 2048;
    this.dirLight.shadow.mapSize.height = 2048;
    this.dirLight.shadow.camera.near = 0.5;
    this.dirLight.shadow.camera.far = 60;
    this.dirLight.shadow.camera.left = -15;
    this.dirLight.shadow.camera.right = 15;
    this.dirLight.shadow.camera.top = 15;
    this.dirLight.shadow.camera.bottom = -15;
    this.dirLight.shadow.bias = -0.0005;
    this.scene.add(this.dirLight);

    // Blue rim light for silhouette edge highlights
    const rimLight = new THREE.DirectionalLight(0x38bdf8, 1.2);
    rimLight.position.set(-16, 14, -18);
    this.scene.add(rimLight);

    // 5. Initialize subsystems
    this.cameraController = new CameraController(this.camera, this.renderer.domElement);

    this.environment = new CastleEnvironment();
    this.scene.add(this.environment.getGroup());

    this.boardView = new BoardView();
    this.scene.add(this.boardView.getGroup());

    this.pieceView = new PieceViewManager();
    this.scene.add(this.pieceView.getGroup());

    this.animationManager = new AnimationManager(
      this.scene,
      this.pieceView,
      soundManager,
      this.cameraController
    );

    // 6. Interaction setup
    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    window.addEventListener('resize', this.onResize);
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.startLoop();
  }

  public getBoardView(): BoardView {
    return this.boardView;
  }

  public getPieceView(): PieceViewManager {
    return this.pieceView;
  }

  public getCameraController(): CameraController {
    return this.cameraController;
  }

  public getAnimationManager(): AnimationManager {
    return this.animationManager;
  }

  public getSquareAtClientCoords(clientX: number, clientY: number): Square | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.mouse.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.mouse.y = -((clientY - rect.top) / rect.height) * 2 + 1;

    this.raycaster.setFromCamera(this.mouse, this.camera);

    // Raycast against tiles
    const tileMeshes = this.boardView.getTileMeshes();
    const tileHits = this.raycaster.intersectObjects(tileMeshes, false);
    if (tileHits.length > 0) {
      const hit = tileHits[0];
      if (hit.object.userData && hit.object.userData.square) {
        return hit.object.userData.square as Square;
      }
    }

    // Raycast against pieces if user clicked directly on a piece model
    const pieces = this.pieceView.getAllPieces().map((p) => p.mesh);
    const pieceHits = this.raycaster.intersectObjects(pieces, false);
    if (pieceHits.length > 0) {
      const hit = pieceHits[0];
      if (hit.object.userData && hit.object.userData.square) {
        return hit.object.userData.square as Square;
      }
    }

    return null;
  }

  public setQuality(quality: 'high' | 'medium' | 'low'): void {
    if (quality === 'low') {
      this.renderer.shadowMap.enabled = false;
      this.renderer.setPixelRatio(1);
      this.dirLight.castShadow = false;
    } else if (quality === 'medium') {
      this.renderer.shadowMap.enabled = true;
      this.renderer.setPixelRatio(1.25);
      this.dirLight.castShadow = true;
      this.dirLight.shadow.mapSize.width = 1024;
      this.dirLight.shadow.mapSize.height = 1024;
    } else {
      this.renderer.shadowMap.enabled = true;
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.dirLight.castShadow = true;
      this.dirLight.shadow.mapSize.width = 2048;
      this.dirLight.shadow.mapSize.height = 2048;
    }

    this.environment.setQuality(quality);
    this.pieceView.setQuality(quality);
  }

  private onResize = (): void => {
    const width = this.container.clientWidth || window.innerWidth;
    const height = this.container.clientHeight || window.innerHeight;

    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();

    this.renderer.setSize(width, height);
  };

  private onVisibilityChange = (): void => {
    if (document.hidden) {
      this.isRunning = false;
      if (this.animFrameId) {
        cancelAnimationFrame(this.animFrameId);
        this.animFrameId = null;
      }
    } else {
      this.isRunning = true;
      this.clock.start();
      this.startLoop();
    }
  };

  private startLoop = (): void => {
    const loop = () => {
      if (!this.isRunning) return;
      const delta = Math.min(this.clock.getDelta(), 0.1);
      const time = this.clock.getElapsedTime();

      // Update subsystems
      this.cameraController.update(delta);
      this.environment.update(delta, time);
      this.boardView.update(time);
      this.animationManager.update(delta);

      this.renderer.render(this.scene, this.camera);
      this.animFrameId = requestAnimationFrame(loop);
    };

    loop();
  };

  public destroy(): void {
    this.isRunning = false;
    if (this.animFrameId) {
      cancelAnimationFrame(this.animFrameId);
    }
    window.removeEventListener('resize', this.onResize);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.renderer.dispose();
  }
}
